import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ChatsTeardrop, Checks, WhatsappLogo, Lightning } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const bubbles = [
  { id: 1, side: "in", text: "Bhaiya Patna se Delhi ki ek cab chahiye kal subah 🚗", delay: 0.2 },
  { id: 2, side: "out", text: "Sure! 6 AM pickup OK rahega? ₹3,200 fare hoga.", delay: 1.1 },
  { id: 3, side: "in", text: "Ok book kar dijiye 👍", delay: 2.0 },
];

export default function EmptyHero() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div
      className="flex-1 relative overflow-hidden bg-[var(--bg)]"
      data-testid="chat-empty-state"
    >
      {/* Background hero image with overlay */}
      <div className="absolute inset-0">
        <img
          src="/rodbez-hero.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center opacity-[0.18] dark:opacity-[0.12] select-none pointer-events-none"
          draggable={false}
        />
        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--bg)] via-transparent to-[var(--bg)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,rgba(37,211,102,0.10),transparent_60%)]" />
      </div>

      {/* Content */}
      <div className="relative h-full w-full flex flex-col items-center justify-center px-4 md:px-8 py-8 md:py-10">
        {/* Top greeting */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] tracking-[0.24em] uppercase text-[var(--muted)] mb-2"
        >
          {new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening"}, {user?.full_name?.split(" ")[0] || "team"}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-center max-w-2xl leading-[1.1]"
        >
          Every customer,{" "}
          <span className="relative inline-block">
            <span className="relative z-10">one inbox.</span>
            <span className="absolute inset-x-0 bottom-1 h-3 bg-[#25D366]/30 rounded-sm z-0" />
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-sm text-[var(--muted)] mt-3 max-w-md text-center leading-relaxed"
        >
          Pick a conversation from the left. Your team replies, customers stay
          happy, bookings flow in.
        </motion.p>

        {/* Animated chat preview */}
        <div className="mt-10 w-full max-w-md space-y-2">
          {bubbles.map((b) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, x: b.side === "in" ? -20 : 20, y: 8 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.45, delay: b.delay }}
              className={`flex ${b.side === "out" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] px-3.5 py-2 text-xs shadow-sm ${
                  b.side === "out"
                    ? "bg-[var(--fg)] text-[var(--bg)] rounded-2xl rounded-tr-sm"
                    : "bg-[var(--surface)] border border-[var(--border)] text-[var(--fg)] rounded-2xl rounded-tl-sm"
                }`}
              >
                {b.text}
                {b.side === "out" && (
                  <span className="ml-2 inline-flex align-middle">
                    <Checks size={11} className="text-[#60A5FA]" weight="bold" />
                  </span>
                )}
              </div>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 2.8 }}
            className="text-[10px] text-[var(--muted)] text-center pt-1"
          >
            — a typical RodBez conversation
          </motion.div>
        </div>

        {/* Live stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 3.2 }}
          className="mt-10 grid grid-cols-3 gap-3 w-full max-w-lg"
        >
          <StatChip icon={ChatsTeardrop} label="Today" value={stats?.today_total ?? "—"} tint="text-[var(--fg)]" />
          <StatChip icon={Lightning} label="Customers" value={stats?.today_unique_customers ?? "—"} tint="text-[#3B82F6]" />
          <StatChip icon={WhatsappLogo} label="All-time" value={stats?.all_time_total ?? "—"} tint="text-[#25D366]" />
        </motion.div>

        {/* Footer hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 3.6 }}
          className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-2 text-[11px] text-[var(--muted)]"
        >
          <ChatsTeardrop size={12} />
          <span>Select a customer from the sidebar to begin</span>
        </motion.div>
      </div>
    </div>
  );
}

function StatChip({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)]/80 backdrop-blur px-3 py-2.5 flex items-center gap-2.5">
      <div className={`h-8 w-8 rounded-lg bg-[var(--bg)] grid place-items-center ${tint}`}>
        <Icon size={16} weight="fill" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] tracking-wider uppercase text-[var(--muted)] leading-tight">
          {label}
        </div>
        <div className="heading font-bold text-base leading-tight">{value}</div>
      </div>
    </div>
  );
}
